import Button from 'devextreme-react/button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { showErrorToast } from '../notifications/toast';
import { formatCalcNumber, tryEvaluateExpression } from './calculatorEngine';
import {
  createHistoryEntry,
  loadCalculatorPrefs,
  saveCalculatorPrefs,
  trimHistory
} from './calculatorPrefs';
import './calculatorWidget.css';

const PAD: string[][] = [
  ['C', '⌫', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '-'],
  ['1', '2', '3', '+'],
  ['0', '.', '=']
];

export interface CalculatorWidgetProps {
  /** localStorage key, namespaced per site. */
  storageKey: string;
  /** Route to a full Calculator app. Omitted on sites that only ship the widget. */
  fullAppPath?: string;
  fullAppLabel?: string;
}

/** Compact simple calculator for a size-1 dashboard card. */
export function CalculatorWidget({
  storageKey,
  fullAppPath,
  fullAppLabel = 'Open full Calculator'
}: CalculatorWidgetProps) {
  const initial = useMemo(() => loadCalculatorPrefs(storageKey), [storageKey]);
  const [expression, setExpression] = useState('');
  const [display, setDisplay] = useState('0');
  const [fresh, setFresh] = useState(true);
  const [memory, setMemory] = useState<number | null>(initial.memory);
  const [history, setHistory] = useState(initial.history);

  useEffect(() => {
    const prefs = loadCalculatorPrefs(storageKey);
    saveCalculatorPrefs(storageKey, {
      ...prefs,
      memory,
      history
    });
  }, [history, memory, storageKey]);

  const applyResult = useCallback((expr: string, value: number) => {
    const formatted = formatCalcNumber(value);
    setExpression(expr);
    setDisplay(formatted);
    setFresh(true);
    setHistory((prev) => trimHistory([createHistoryEntry(expr, formatted, 'simple'), ...prev]));
  }, []);

  const evaluateCurrent = useCallback(() => {
    const needsDisplay = !expression || /[+\-×÷(]$/.test(expression);
    const expr = !expression ? display : needsDisplay ? `${expression}${display}` : expression;
    const result = tryEvaluateExpression(expr);
    if (!result.ok) {
      showErrorToast(result.error);
      return;
    }
    applyResult(expr, result.value);
  }, [applyResult, display, expression]);

  const press = useCallback(
    (key: string) => {
      if (key === 'C') {
        setExpression('');
        setDisplay('0');
        setFresh(true);
        return;
      }
      if (key === '⌫') {
        if (fresh) {
          setDisplay('0');
          return;
        }
        setDisplay((d) => (d.length <= 1 ? '0' : d.slice(0, -1)));
        return;
      }
      if (key === '=') {
        evaluateCurrent();
        return;
      }
      if (key === '%') {
        const base = expression ? `${expression}${display}` : display;
        const result = tryEvaluateExpression(`${base}%`);
        if (!result.ok) {
          showErrorToast(result.error);
          return;
        }
        applyResult(`${base}%`, result.value);
        return;
      }
      if (key === '÷' || key === '×' || key === '+' || key === '-') {
        if (fresh && expression) {
          setExpression((e) => `${e}${key}`);
          return;
        }
        setExpression((e) => `${e || display}${key}`);
        setFresh(true);
        return;
      }
      if (/[\d.]/.test(key)) {
        if (fresh) {
          setDisplay(key === '.' ? '0.' : key);
          setFresh(false);
          return;
        }
        setDisplay((d) => {
          if (key === '.' && d.includes('.')) return d;
          if (d === '0' && key !== '.') return key;
          return `${d}${key}`;
        });
      }
    },
    [applyResult, display, evaluateCurrent, expression, fresh]
  );

  const onMemory = useCallback(
    (action: 'MC' | 'MR' | 'MS' | 'M+' | 'M-') => {
      const current = Number(display);
      if (action === 'MC') {
        setMemory(null);
        return;
      }
      if (action === 'MR') {
        if (memory == null) return;
        setDisplay(formatCalcNumber(memory));
        setFresh(true);
        return;
      }
      if (!Number.isFinite(current)) return;
      if (action === 'MS') {
        setMemory(current);
        return;
      }
      setMemory((prev) => {
        const base = prev ?? 0;
        return action === 'M+' ? base + current : base - current;
      });
    },
    [display, memory]
  );

  return (
    <section className="dashboard-card calculator-widget" aria-label="Calculator">
      <div className="dashboard-card__header">
        <div>
          <h3 className="dashboard-card__title">Calculator</h3>
          <p className="calculator-widget__muted">Quick simple math</p>
        </div>
        {memory != null ? (
          <span className="calculator-widget__memory-pill" title="Memory">
            M {formatCalcNumber(memory)}
          </span>
        ) : null}
      </div>

      <div className="calculator-widget__display" aria-live="polite">
        <div className="calculator-widget__expression">{expression || ' '}</div>
        <div className="calculator-widget__value">{display}</div>
      </div>

      <div className="calculator-widget__memory">
        {(['MC', 'MR', 'MS', 'M+', 'M-'] as const).map((key) => (
          <Button key={key} text={key} stylingMode="outlined" onClick={() => onMemory(key)} />
        ))}
      </div>

      <div className="calculator-widget__pad">
        {PAD.flatMap((row, rowIndex) =>
          row.map((key, colIndex) => (
            <Button
              key={`${rowIndex}-${colIndex}-${key}`}
              text={key}
              type={key === '=' ? 'default' : 'normal'}
              stylingMode={/[÷×+\-=]/.test(key) ? 'contained' : 'outlined'}
              elementAttr={key === '0' ? { style: { gridColumn: 'span 2' } } : undefined}
              onClick={() => press(key)}
            />
          ))
        )}
      </div>

      {fullAppPath ? (
        <div className="calculator-widget__footer">
          <Link to={fullAppPath}>{fullAppLabel}</Link>
        </div>
      ) : null}
    </section>
  );
}
